// tenant/AddressInfoPage.tsx
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import TenantSignupLayout from '../TenantSignupLayout';
import { ListOfStates } from '../../components/TenantApp/ListOfStates'; // adjust path if needed
import { API } from 'aws-amplify';
import * as queries from '../../graphql/queries'; // adjust import path

type ZipLists = {
  tacoma: string[];
  richmond: string[];
};

const AddressInfoPage: React.FC = () => {
  const history = useHistory();

  const [form, setForm] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: 'CA',
    zip: '',
    building: '',
    parking: '',
    specialInstructions: '',
    referralCode: '',
  });

  const [zipLists, setZipLists] = useState<ZipLists>({ tacoma: [], richmond: [] });
  const [loadingZips, setLoadingZips] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  // Pull zipcodes from your API (same as mobile logic)
  useEffect(() => {
    const fetchZips = async () => {
      try {
        setLoadingZips(true);
        const zipcodes: any = await API.graphql({
          query: queries.listZipcodess,
          authMode: 'AWS_IAM',
        });

        const items = zipcodes.data.listZipcodess.items || [];
        const tacomaData = items.find((i: any) => i.id === 'tacoma');
        const richmondData = items.find((i: any) => i.id === 'richmond');

        setZipLists({
          tacoma: tacomaData ? tacomaData.zipcodes : [],
          richmond: richmondData ? richmondData.zipcodes : [],
        });
      } catch (e) {
        console.error('Error fetching zipcodes', e);
      } finally {
        setLoadingZips(false);
      }
    };

    fetchZips();
  }, []);

  const updateField = (key: keyof typeof form, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const newErrors: { [k: string]: string } = {};

    if (!form.addressLine1.trim()) newErrors.addressLine1 = 'Required';
    if (!form.city.trim()) newErrors.city = 'Required';
    if (!form.state.trim()) newErrors.state = 'Required';
    if (!form.zip.trim() || form.zip.length !== 5) newErrors.zip = '5-digit ZIP';
    if (!form.building.trim()) newErrors.building = 'Required';
    if (!form.parking.trim()) newErrors.parking = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFacilityFromZip = (): 'Richmond' | 'Tacoma' | null => {
    if (zipLists.richmond.includes(form.zip)) return 'Richmond';
    if (zipLists.tacoma.includes(form.zip)) return 'Tacoma';
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const facility = getFacilityFromZip();
    if (!facility) {
      alert('Sorry, there are no available facilities near you yet.');
      return;
    }

    setSubmitting(true);

    const payload = {
      ...form,
      showFacility: facility,
    };

    history.push('/signup/select-facility', payload);
  };

  // Small helper to render label + input with consistent styling
  const renderTextField = (
    name: keyof typeof form,
    label: string,
    placeholder?: string,
    opts?: React.InputHTMLAttributes<HTMLInputElement>
  ) => (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: 'block',
          fontSize: 14,
          fontWeight: 500,
          marginBottom: 4,
          color: '#333',
        }}
      >
        {label}
      </label>
      <input
        {...opts}
        value={form[name]}
        onChange={e => updateField(name, e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 6,
          border: errors[name] ? '1px solid #e53935' : '1px solid #ccc',
          fontSize: 14,
          boxSizing: 'border-box',
        }}
      />
      {errors[name] && (
        <div style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>{errors[name]}</div>
      )}
    </div>
  );

  return (
    <TenantSignupLayout
      title="Where should we pick up your items?"
      subtitle="We’ll match you with the closest Bins partner facility."
      step={1}
      totalSteps={7}
    >
      <form onSubmit={handleSubmit}>
        {/* Address lines */}
        {renderTextField('addressLine1', 'Address Line 1', '123 Main St')}
        {renderTextField('addressLine2', 'Address Line 2 (optional)', 'Apt, unit, etc.')}

        {/* City / State / ZIP in one row on desktop */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            {renderTextField('city', 'City', 'Berkeley')}
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 4,
                color: '#333',
              }}
            >
              State
            </label>
            <select
              value={form.state}
              onChange={e => updateField('state', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: errors.state ? '1px solid #e53935' : '1px solid #ccc',
                fontSize: 14,
                boxSizing: 'border-box',
                backgroundColor: '#fff',
              }}
            >
              {ListOfStates.map(s => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            {errors.state && (
              <div style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>{errors.state}</div>
            )}
          </div>

          <div>
            {renderTextField('zip', 'ZIP', '94704', { maxLength: 5 })}
          </div>
        </div>

        {/* Building / Parking */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 4,
                color: '#333',
              }}
            >
              Building Type
            </label>
            <select
              value={form.building}
              onChange={e => updateField('building', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: errors.building ? '1px solid #e53935' : '1px solid #ccc',
                fontSize: 14,
                backgroundColor: '#fff',
              }}
            >
              <option value="">Select an option</option>
              <option value="House">House</option>
              <option value="Apartment">Apartment</option>
            </select>
            {errors.building && (
              <div style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>
                {errors.building}
              </div>
            )}
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 4,
                color: '#333',
              }}
            >
              Where can we park?
            </label>
            <select
              value={form.parking}
              onChange={e => updateField('parking', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: errors.parking ? '1px solid #e53935' : '1px solid #ccc',
                fontSize: 14,
                backgroundColor: '#fff',
              }}
            >
              <option value="">Select an option</option>
              <option value="In Front of Building">In front of building</option>
              <option value="Driveway">Driveway</option>
              <option value="Parking Lot">Parking lot</option>
            </select>
            {errors.parking && (
              <div style={{ color: '#e53935', fontSize: 12, marginTop: 4 }}>
                {errors.parking}
              </div>
            )}
          </div>
        </div>

        {/* Special instructions & referral */}
        <div style={{ marginBottom: 16 }}>
          <label
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 4,
              color: '#333',
            }}
          >
            Special Instructions (optional)
          </label>
          <textarea
            value={form.specialInstructions}
            onChange={e => updateField('specialInstructions', e.target.value)}
            placeholder="Gate code, buzzer instructions, etc."
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: '1px solid #ccc',
              fontSize: 14,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 4,
              color: '#333',
            }}
          >
            Referral Code (optional)
          </label>
          <input
            value={form.referralCode}
            onChange={e => updateField('referralCode', e.target.value)}
            placeholder="Code provided by your facility"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: '1px solid #ccc',
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 8,
          }}
        >
          <button
            type="button"
            onClick={() => history.push('/')}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 6,
              border: '1px solid #ccc',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || loadingZips}
            style={{
              flex: 1.4,
              padding: '10px 12px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: '#7B1FA2',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: submitting || loadingZips ? 0.7 : 1,
            }}
          >
            {loadingZips ? 'Checking service area…' : 'Find a unit'}
          </button>
        </div>
      </form>
    </TenantSignupLayout>
  );
};

export default AddressInfoPage;
